import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {
  Building2,
  CheckCheck,
  CheckCircle2,
  KeyRound,
  RotateCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import {Modal} from 'react-bootstrap'
import Select from 'react-select'

import * as API from '../../api'
import CompanyBulkAccessModal from './CompanyBulkAccessModal'

type Props = {
  categoryId: string
  categoryName?: string
}

type Column = {
  id: number
  kind: 'product' | 'subproduct'
  label: string
  parent?: string
  item: any
}

const emptyData = {
  companys: [] as any[],
  menu: {products: [] as any[]},
  products: [] as any[],
  subProducts: [] as any[],
}

const companyInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'EM'

const getCompanyType = (company: any) => {
  if (company.type_name) return company.type_name
  if (typeof company.company_type === 'string') return company.company_type
  if (`${company.id_type_company}` === '1') return 'Indústria'
  if (`${company.id_type_company}` === '2') return 'Distribuidor'
  return 'Outro tipo'
}

const CompanyPermissions: FC<Props> = ({categoryId, categoryName}) => {
  const [data, setData] = useState<any>(emptyData)
  const [selectedCompanies, setSelectedCompanies] = useState<any[]>([])
  const [typeFilter, setTypeFilter] = useState('all')
  const [permissionQuery, setPermissionQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set())
  const [selectedColumn, setSelectedColumn] = useState<Column | null>(null)
  const [columnBulkSaving, setColumnBulkSaving] = useState(false)
  const [selectedMatrixCompany, setSelectedMatrixCompany] = useState<any>(null)
  const [companyBulkSaving, setCompanyBulkSaving] = useState(false)
  const [feedback, setFeedback] = useState('')

  const update = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true)
      setLoadError(false)

      try {
        const response = await API.getCategoryPermissions(categoryId)
        const payload = response?.data || {}
        setData({
          ...payload,
          companys: Array.isArray(payload.companys) ? payload.companys : [],
          menu: {products: Array.isArray(payload.menu?.products) ? payload.menu.products : []},
          products: Array.isArray(payload.products) ? payload.products : [],
          subProducts: Array.isArray(payload.subProducts) ? payload.subProducts : [],
        })
      } catch {
        setLoadError(true)
      } finally {
        if (showLoading) setLoading(false)
      }
    },
    [categoryId]
  )

  useEffect(() => {
    setSelectedCompanies([])
    setPermissionQuery('')
    setTypeFilter('all')
    setFeedback('')
    update()
  }, [categoryId, update])

  const allColumns = useMemo<Column[]>(
    () =>
      (data.menu?.products || []).flatMap((product: any) => [
        {
          id: product.id_product,
          kind: 'product' as const,
          label: product.admin_name || product.name,
          item: product,
        },
        ...(product.subproducts || []).map((subproduct: any) => ({
          id: subproduct.id_subproduct,
          kind: 'subproduct' as const,
          label: subproduct.admin_name || subproduct.name,
          parent: product.admin_name || product.name,
          item: subproduct,
        })),
      ]),
    [data.menu]
  )

  const columns = useMemo(() => {
    const normalizedQuery = permissionQuery.trim().toLocaleLowerCase('pt-BR')
    if (!normalizedQuery) return allColumns
    return allColumns.filter((column) =>
      `${column.label} ${column.parent || ''}`.toLocaleLowerCase('pt-BR').includes(normalizedQuery)
    )
  }, [allColumns, permissionQuery])

  const companyOptions = useMemo(
    () =>
      (data.companys || []).map((company: any) => ({
        value: company.guid,
        label: company.name,
        ...company,
      })),
    [data.companys]
  )

  const companyTypes = useMemo(() => {
    const unique = new Map<string, string>()
    ;(data.companys || []).forEach((company: any) => {
      unique.set(`${company.id_type_company}`, getCompanyType(company))
    })
    return Array.from(unique.entries())
  }, [data.companys])

  const displayedCompanies = useMemo(() => {
    const selectedIds = new Set(selectedCompanies.map((company) => `${company.guid}`))
    return (data.companys || []).filter((company: any) => {
      const selected = !selectedIds.size || selectedIds.has(`${company.guid}`)
      const matchesType = typeFilter === 'all' || `${company.id_type_company}` === typeFilter
      return selected && matchesType
    })
  }, [data.companys, selectedCompanies, typeFilter])

  const isEnabled = useCallback(
    (company: any, column: Column) => {
      const permissions = column.kind === 'product' ? data.products : data.subProducts
      const idKey = column.kind === 'product' ? 'id_product' : 'id_subproduct'
      return Boolean(
        permissions.some(
          (permission: any) =>
            `${permission[idKey]}` === `${column.id}` &&
            `${permission.id_company}` === `${company.id_company}`
        )
      )
    },
    [data.products, data.subProducts]
  )

  function updateLocalPermission(company: any, column: Column, enabled: boolean) {
    const collection = column.kind === 'product' ? 'products' : 'subProducts'
    const idKey = column.kind === 'product' ? 'id_product' : 'id_subproduct'

    setData((current: any) => {
      const permissions = current[collection] || []
      const matches = (permission: any) =>
        `${permission[idKey]}` === `${column.id}` &&
        `${permission.id_company}` === `${company.id_company}`

      return {
        ...current,
        [collection]: enabled
          ? permissions.some(matches)
            ? permissions
            : [...permissions, {[idKey]: column.id, id_company: company.id_company}]
          : permissions.filter((permission: any) => !matches(permission)),
      }
    })
  }

  async function changePermission(company: any, column: Column, checked: boolean) {
    const cellKey = `${company.id_company}-${column.kind}-${column.id}`
    setSavingCells((current) => new Set(current).add(cellKey))
    setFeedback('')
    updateLocalPermission(company, column, checked)

    try {
      const payload =
        column.kind === 'product'
          ? {id_product: column.id, id_company: company.id_company}
          : {id_subproduct: column.id, id_company: company.id_company}
      let response: any

      if (column.kind === 'product') {
        response = checked
          ? await API.addProductCompanyPermission(payload)
          : await API.removeProductCompanyPermission(payload)
      } else {
        response = checked
          ? await API.addSubProductCompanyPermission(payload)
          : await API.removeSubProductCompanyPermission(payload)
      }
      if (response?.success === false) throw new Error('Permission update failed')
      await update(false)
    } catch {
      await update(false)
      setFeedback('Não foi possível salvar uma das alterações. Tente novamente.')
    } finally {
      setSavingCells((current) => {
        const next = new Set(current)
        next.delete(cellKey)
        return next
      })
    }
  }

  async function changeColumnPermissions(selectAll: boolean) {
    if (!selectedColumn) return
    setColumnBulkSaving(true)
    setFeedback('')

    try {
      let response: any
      if (selectedColumn.kind === 'subproduct') {
        response = selectAll
          ? await API.selectAllCompanysIntoSubproduct(selectedColumn.id)
          : await API.unselectAllCompanysIntoSubproduct(selectedColumn.id)
      } else {
        response = selectAll
          ? await API.selectAllCompanysIntoProduct(selectedColumn.id)
          : await API.unselectAllCompanysIntoProduct(selectedColumn.id)
      }
      if (response?.success === false) throw new Error('Bulk column update failed')
      await update(false)
      setFeedback(
        `${selectedColumn.label} foi ${
          selectAll ? 'concedida' : 'removida'
        } para todas as empresas.`
      )
      setSelectedColumn(null)
    } catch {
      await update(false)
      setFeedback('Não foi possível concluir a alteração em massa. Tente novamente.')
    } finally {
      setColumnBulkSaving(false)
    }
  }

  async function changeCompanyPermissions(selectAll: boolean) {
    if (!selectedMatrixCompany) return
    const targets = allColumns.filter(
      (column) => isEnabled(selectedMatrixCompany, column) !== selectAll
    )
    setCompanyBulkSaving(true)
    setFeedback('')

    try {
      const responses = await Promise.all(
        targets.map((column) => {
          if (column.kind === 'product') {
            const payload = {id_product: column.id, id_company: selectedMatrixCompany.id_company}
            return selectAll
              ? API.addProductCompanyPermission(payload)
              : API.removeProductCompanyPermission(payload)
          }
          const payload = {
            id_subproduct: column.id,
            id_company: selectedMatrixCompany.id_company,
          }
          return selectAll
            ? API.addSubProductCompanyPermission(payload)
            : API.removeSubProductCompanyPermission(payload)
        })
      )
      if (responses.some((response: any) => response?.success === false)) {
        throw new Error('Bulk company update failed')
      }
      await update(false)
      setFeedback(
        `${
          selectAll ? 'Todas as permissões foram concedidas' : 'Todas as permissões foram removidas'
        } para ${selectedMatrixCompany.name}.`
      )
      setSelectedMatrixCompany(null)
    } catch {
      await update(false)
      setFeedback('Não foi possível concluir a alteração para esta empresa. Tente novamente.')
    } finally {
      setCompanyBulkSaving(false)
    }
  }

  const enabledCount = new Set([
    ...data.products.map((item: any) => `${item.id_company}-product-${item.id_product}`),
    ...data.subProducts.map((item: any) => `${item.id_company}-subproduct-${item.id_subproduct}`),
  ]).size
  const possiblePermissions = (data.companys?.length || 0) * allColumns.length
  const coverage = possiblePermissions ? Math.round((enabledCount / possiblePermissions) * 100) : 0
  const selectedCompanyActiveCount = selectedMatrixCompany
    ? allColumns.filter((column) => isEnabled(selectedMatrixCompany, column)).length
    : 0
  const hasFilters = Boolean(selectedCompanies.length || permissionQuery || typeFilter !== 'all')

  if (loading) {
    return (
      <div className='ua-card ua-loading'>
        <span className='ua-spinner' /> Carregando matriz de permissões…
      </div>
    )
  }

  if (loadError) {
    return (
      <div className='ua-empty'>
        <div>
          <span className='ua-empty__icon'>
            <KeyRound size={22} />
          </span>
          <h3>Não foi possível carregar esta categoria</h3>
          <p>Tente novamente para visualizar as empresas e seus acessos.</p>
          <button type='button' className='ua-secondary-button mt-4' onClick={() => update()}>
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Modal
        id='company_column_permissions_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-450px ua-admin-modal-dialog'
        show={Boolean(selectedColumn)}
        onHide={() => !columnBulkSaving && setSelectedColumn(null)}
      >
        {selectedColumn && (
          <div className='ua-bulk-modal'>
            <header className='ua-bulk-modal__header'>
              <span className='ua-section__heading-icon'>
                <SlidersHorizontal size={19} />
              </span>
              <div>
                <small>Alteração em massa</small>
                <h3>{selectedColumn.label}</h3>
              </div>
              <button
                type='button'
                className='ua-icon-button'
                aria-label='Fechar'
                disabled={columnBulkSaving}
                onClick={() => setSelectedColumn(null)}
              >
                <X size={15} />
              </button>
            </header>
            <div className='ua-bulk-modal__body'>
              <p>Escolha como esta permissão deve ser aplicada a todas as empresas.</p>
              <button
                type='button'
                className='ua-bulk-action is-positive'
                disabled={columnBulkSaving}
                onClick={() => changeColumnPermissions(true)}
              >
                <span>
                  {columnBulkSaving ? <span className='ua-spinner' /> : <CheckCheck size={18} />}
                </span>
                <span>
                  <strong>Conceder para todas</strong>
                  <small>Ativa esta permissão em todas as empresas.</small>
                </span>
              </button>
              <button
                type='button'
                className='ua-bulk-action is-negative'
                disabled={columnBulkSaving}
                onClick={() => changeColumnPermissions(false)}
              >
                <span>
                  {columnBulkSaving ? <span className='ua-spinner' /> : <RotateCcw size={18} />}
                </span>
                <span>
                  <strong>Remover de todas</strong>
                  <small>Desativa esta permissão em todas as empresas.</small>
                </span>
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        id='company_row_permissions_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-500px ua-admin-modal-dialog'
        show={Boolean(selectedMatrixCompany)}
        onHide={() => !companyBulkSaving && setSelectedMatrixCompany(null)}
      >
        {selectedMatrixCompany && (
          <CompanyBulkAccessModal
            company={selectedMatrixCompany}
            scopeLabel={categoryName || 'Categoria atual'}
            itemLabel='permissões'
            totalCount={allColumns.length}
            activeCount={selectedCompanyActiveCount}
            loading={companyBulkSaving}
            onChange={changeCompanyPermissions}
            onClose={() => setSelectedMatrixCompany(null)}
          />
        )}
      </Modal>

      <div className='ua-stats'>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <Building2 size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>{data.companys.length}</strong>
            <small className='ua-stat__label'>Empresas na matriz</small>
          </span>
        </div>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <KeyRound size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>{allColumns.length}</strong>
            <small className='ua-stat__label'>Permissões disponíveis</small>
          </span>
        </div>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <CheckCircle2 size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>{enabledCount}</strong>
            <small className='ua-stat__label'>Acessos concedidos</small>
          </span>
        </div>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <ShieldCheck size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>{coverage}%</strong>
            <small className='ua-stat__label'>Cobertura de acesso</small>
          </span>
        </div>
      </div>

      <div className='ua-toolbar'>
        <div className='ua-field ua-field--wide'>
          <label htmlFor='permission-company-filter'>Empresas</label>
          <Select
            inputId='permission-company-filter'
            isMulti
            options={companyOptions}
            value={selectedCompanies}
            closeMenuOnSelect={false}
            placeholder='Todas as empresas'
            noOptionsMessage={() => 'Nenhuma empresa encontrada'}
            onChange={(selected: any) => setSelectedCompanies(selected || [])}
            classNamePrefix='admin-select'
          />
        </div>
        <div className='ua-field'>
          <label htmlFor='permission-company-type'>Tipo</label>
          <div className='ua-select'>
            <select
              id='permission-company-type'
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value='all'>Todos os tipos</option>
              {companyTypes.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className='ua-field'>
          <label htmlFor='permission-company-query'>Produto ou menu</label>
          <div className='ua-search'>
            <Search size={15} />
            <input
              id='permission-company-query'
              value={permissionQuery}
              placeholder='Buscar permissão'
              onChange={(event) => setPermissionQuery(event.target.value)}
            />
            {permissionQuery && (
              <button
                type='button'
                className='ua-search__clear'
                aria-label='Limpar busca de permissão'
                onClick={() => setPermissionQuery('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        {hasFilters && (
          <button
            type='button'
            className='ua-secondary-button'
            onClick={() => {
              setSelectedCompanies([])
              setTypeFilter('all')
              setPermissionQuery('')
            }}
          >
            <X size={14} /> Limpar
          </button>
        )}
      </div>

      {feedback && (
        <div
          className={`ua-feedback ${
            feedback.startsWith('Não foi possível') ? 'ua-feedback--danger' : ''
          }`}
        >
          {feedback}
        </div>
      )}

      <div className='ua-card'>
        <div className='ua-card__header'>
          <div>
            <h3>{categoryName || 'Matriz de permissões'}</h3>
            <p>
              {displayedCompanies.length} empresa(s) · {columns.length} permissão(ões) visível(is)
            </p>
          </div>
          <span className='ua-matrix-tip'>
            Clique em uma coluna ou empresa para aplicar em massa
          </span>
        </div>

        {displayedCompanies.length && columns.length ? (
          <div className='ua-table-wrap ua-matrix-wrap'>
            <table className='ua-table ua-matrix'>
              <thead>
                <tr>
                  <th className='ua-sticky-column'>Empresa</th>
                  {columns.map((column) => (
                    <th key={`${column.kind}-${column.id}`} className='ua-matrix__permission'>
                      <button
                        type='button'
                        className='ua-matrix-header-button'
                        title={`Aplicar ${column.label} a todas as empresas`}
                        onClick={() => setSelectedColumn(column)}
                      >
                        {column.parent && <small>{column.parent}</small>}
                        <span>{column.label}</span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedCompanies.map((company: any) => (
                  <tr key={company.guid || company.id_company}>
                    <td className='ua-sticky-column'>
                      <button
                        type='button'
                        className='ua-user-trigger'
                        title={`Gerenciar todas as permissões de ${company.name}`}
                        onClick={() => setSelectedMatrixCompany(company)}
                      >
                        <span className='ua-user-cell'>
                          <span className='ua-avatar'>{companyInitials(company.name)}</span>
                          <span className='ua-user-cell__copy'>
                            <strong>{company.name}</strong>
                            <small>{getCompanyType(company)}</small>
                          </span>
                        </span>
                        <span className='ua-user-trigger__action'>
                          <SlidersHorizontal size={14} />
                        </span>
                      </button>
                    </td>
                    {columns.map((column) => {
                      const cellKey = `${company.id_company}-${column.kind}-${column.id}`
                      return (
                        <td key={cellKey} className='ua-matrix__cell'>
                          <label className='ua-switch'>
                            <input
                              type='checkbox'
                              checked={isEnabled(company, column)}
                              disabled={savingCells.has(cellKey)}
                              aria-label={`${column.label} para ${company.name}`}
                              onChange={(event) =>
                                changePermission(company, column, event.target.checked)
                              }
                            />
                            <span />
                          </label>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='ua-empty ua-empty--inside'>
            <div>
              <span className='ua-empty__icon'>
                <Search size={22} />
              </span>
              <h3>Nenhum resultado nesta matriz</h3>
              <p>Ajuste os filtros para visualizar outras combinações de acesso.</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CompanyPermissions
